import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NCAHTLogo from '../assets/images/NCAHTlogo.png'
import NCAHTLogoWhite from '../assets/images/NCAHTlogoWhite.png'
import ICMRLogo  from '../assets/images/ICMRlogo.png'
import ICMRLogoWhite  from '../assets/images/ICMRlogoWhite.png'
import NISHLogo  from '../assets/images/NISHlogo.png'
import NISHLogoWhite  from '../assets/images/NISHlogoWhite.png'
// import PublicSRTModal from './PublicSRTModal'
// ─────────────────────────────────────────────────────────────
// BRAND TOKENS (Vidyagni brand kit)
// ─────────────────────────────────────────────────────────────
const T = {
  agniCore:   '#EA580C',
  agniGlow:   '#F97316',
  agniTip:    '#FDE68A',
  vidyaGold:  '#C8A84B',
  nightInk:   '#0F1A2E',
  deepNavy:   '#0a1628',
  vedaCream:  '#FEF8EE',
  ashWhite:   '#FDFCFA',
  slate:      '#111827',
  muted:      '#6B7280',
  blue:       '#2563EB',
  violet:     '#9333EA',
  green:      '#16A34A',
  teal:       '#0891B2',
  amber:      '#B45309',
}

// ─────────────────────────────────────────────────────────────
// VIDYAGNI MARK
// ─────────────────────────────────────────────────────────────
function Mark({ size = 40, light = false }) {
  const bg   = light ? '#FEF3E2' : '#17243A'
  const arch = light ? '#92400E' : T.vidyaGold
  const rx   = Math.round(size * 0.25)
  return (
    <svg width={size} height={size} viewBox="0 0 68 68" fill="none" style={{ flexShrink: 0 }}>
      <rect width="68" height="68" rx={rx} fill={bg} />
      <path d="M34 8C34 8 22 22 22 34C22 45 34 52 34 52C34 52 46 45 46 34C46 22 34 8 34 8Z" fill="#C2410C" />
      <path d="M34 13C34 13 25 25 25 34C25 43 34 49 34 49C34 49 43 43 43 34C43 25 34 13 34 13Z" fill={T.agniCore} />
      <path d="M34 18C34 18 28 28 28 34C28 40 34 46 34 46C34 46 40 40 40 34C40 28 34 18 34 18Z" fill={T.agniGlow} />
      <path d="M34 26C34 26 30 31 30 35C30 39 34 43 34 43C34 43 38 39 38 35C38 31 34 26 34 26Z" fill={T.agniTip} />
      <path d="M26 55Q30 49 34 55Q38 49 42 55" stroke={arch} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="20" y1="59" x2="48" y2="59" stroke={arch} strokeWidth="2.8" strokeLinecap="round" />
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────
// DATA (unchanged)
// ─────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: '🃏', title: 'Flashcard Generator', desc: 'Create multilingual flashcards in 5 Indian languages with custom images. Print-ready A4 PDFs with NISH branding, generated in under a minute.', color: T.agniCore, bg: '#FEF0EB', tag: 'Most Used' },
  { icon: '📝', title: 'Worksheet Creator', desc: 'Structured worksheets aligned to NCF-FS 2022 foundational competencies — language, numeracy, and cognitive domains with auto-layout.', color: T.blue, bg: '#EFF6FF', tag: 'Popular' },
  { icon: '📊', title: 'Progress Tracker', desc: 'Visual milestone tracking across all 5 NCF developmental domains. Shareable reports for parents with clear, jargon-free language.', color: T.violet, bg: '#F5F3FF', tag: null },
  { icon: '🎮', title: 'School Readiness', desc: 'Play-based evaluation tools to assess foundational readiness. Clinician-grade observation checklists with scoring and recommendations.', color: T.green, bg: '#F0FDF4', tag: 'Free for Parents', srt: true },
  { icon: '📅', title: 'Work Plans', desc: 'Personalised weekly learning plans built around each child\'s unique goals, support needs, and current developmental stage.', color: T.teal, bg: '#ECFEFF', tag: null },
  { icon: '🗂️', title: 'Image Collection', desc: 'A shared teaching image library — categorised by theme, NCF domain, and language — ready for use across all platform modules.', color: T.amber, bg: '#FFFBEB', tag: null },
  { icon: '👨‍👩‍👧', title: 'Parent Dashboard', desc: 'Parents view assigned materials, monitor progress at home, and communicate directly with educators through a dedicated, simple portal.', color: T.agniCore, bg: '#FEF0EB', tag: 'New' },
  { icon: '🏫', title: 'School Management', desc: 'Multi-tenant SaaS admin: manage schools, classrooms, subscription tiers, seat enforcement, and full role-based access control.', color: T.blue, bg: '#EFF6FF', tag: 'Admin' },
]

const STATS = [
  { v: '5',   l: 'Indian Languages',  s: 'ML · HI · TA · TE · EN' },
  { v: '8',   l: 'Platform Modules',  s: 'Fully integrated' },
  { v: 'NCF', l: 'FS 2022 Aligned',   s: 'NEP 2020 compliant' },
  { v: '3–8', l: 'Age Range',         s: 'Foundational years' },
]

const DOMAINS = [
  { id: 'Physical Development',   color: T.green,    pct: 78, desc: 'Gross & fine motor · sensory integration · body awareness' },
  { id: 'Socio-emotional',        color: T.agniCore, pct: 65, desc: 'Self-regulation · empathy · peer interaction · identity' },
  { id: 'Cognitive Development',  color: T.violet,   pct: 82, desc: 'Problem solving · memory · attention · executive function' },
  { id: 'Language & Literacy',    color: T.blue,     pct: 71, desc: 'Listening · speaking · emergent reading · communication' },
  { id: 'Numeracy',               color: T.teal,     pct: 58, desc: 'Number sense · patterns · spatial reasoning · measurement' },
]

const HOW = [
  { n: '01', title: 'Create your school',   desc: 'Register your institution, set up classrooms, define subscription tiers, and invite staff with role-based access.', c: T.blue },
  { n: '02', title: 'Add children',         desc: 'Build individual profiles with assessment data, language preferences, disability type, and domain-specific goals.', c: T.violet },
  { n: '03', title: 'Generate materials',   desc: 'Use any of 8 integrated modules to produce flashcards, worksheets, work plans, assessments, and more.', c: T.agniCore },
  { n: '04', title: 'Track & share',        desc: 'Monitor developmental milestones over time and push formatted progress reports directly to the parent portal.', c: T.green },
]

const TESTIMONIALS = [
  { q: 'The flashcard generator saves me hours every week. I can now create Malayalam and Tamil cards in minutes for all my students.', name: 'Priya Nair', role: 'Special Educator, Thiruvananthapuram', av: 'PN', c: T.agniCore },
  { q: 'The progress tracker transformed how we communicate with parents. They finally understand their child\'s developmental journey in clear terms.', name: 'Dr. Anitha Rajan', role: 'Clinician, NISH NCAHT', av: 'AR', c: T.blue },
  { q: 'My son\'s teacher shares worksheets directly through the parent portal. The visual progress reports are easy to understand and very motivating.', name: 'Sujith Menon', role: 'Parent of a child with autism', av: 'SM', c: T.violet },
]

const WHO_FOR = [
  { role: 'Special Educators', icon: '👩‍🏫', color: T.agniCore, bg: '#FEF0EB', points: ['Generate print-ready flashcards in 5 languages','Build structured worksheets from competency templates','Create personalised weekly work plans per child','Assign materials directly to children and parents'] },
  { role: 'Clinicians', icon: '🩺', color: T.blue, bg: '#EFF6FF', points: ['Conduct structured school readiness evaluations','Track developmental milestones across NCF domains','Generate professional, shareable progress reports','View full child history across assessment sessions'] },
  { role: 'Parents', icon: '👨‍👩‍👧', color: T.green, bg: '#F0FDF4', points: ['Access materials assigned by teacher or clinician','Monitor child progress with visual dashboards','Communicate with the educator team in one place','Download PDF worksheets for use at home'] },
  { role: 'School Admins', icon: '🏫', color: T.violet, bg: '#F5F3FF', points: ['Manage multiple schools and classroom rosters','Control subscription seats and user roles','View platform usage and module activity','Onboard new staff with role-based invites'] },
]

const PHOTOS = [
  { src: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=700&q=80', label: 'Visual learning tools', tall: true },
  { src: 'https://images.unsplash.com/photo-1604881991720-f91add269bed?w=500&q=80', label: 'Creative expression' },
  { src: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=500&q=80', label: 'Play-based learning' },
  { src: 'https://images.unsplash.com/photo-1588072432836-e10032774350?w=500&q=80', label: 'Educator support' },
  { src: 'https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=500&q=80', label: 'Social interaction' },
]

// ─────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate   = useNavigate()
  const domainsRef = useRef(null)
  const [domainsVis, setDomainsVis] = useState(false)
  const [scrollY,    setScrollY]    = useState(0)
  const [tIdx,       setTIdx]       = useState(0)
  // const [srtOpen,    setSrtOpen]    = useState(false)
  // ── PATCH 1: mobile menu state ──────────────────────────────
  const [menuOpen,   setMenuOpen]   = useState(false)

  useEffect(() => {
    const fn = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  useEffect(() => {
    if (!domainsRef.current) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setDomainsVis(true) }, { threshold: 0.2 })
    obs.observe(domainsRef.current)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    const t = setInterval(() => setTIdx(i => (i + 1) % TESTIMONIALS.length), 4800)
    return () => clearInterval(t)
  }, [])

  // Close menu when user scrolls away
  useEffect(() => { if (scrollY > 80) setMenuOpen(false) }, [scrollY])

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: T.ashWhite, color: T.slate, overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes fadeUp   { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes fadeIn   { from { opacity:0 } to { opacity:1 } }
        @keyframes drift    { 0%,100%{ transform:translateX(-50%) translateY(0) } 50%{ transform:translateX(-50%) translateY(-6px) } }
        @keyframes shimmer  { 0%{ background-position:-200% center } 100%{ background-position:200% center } }
        @keyframes popIn    { from{ opacity:0; transform:scale(0.94) } to{ opacity:1; transform:scale(1) } }
        @keyframes slideIn  { from{ opacity:0; transform:translateX(-12px) } to{ opacity:1; transform:translateX(0) } }
        @keyframes srtPulse { 0%,100%{ box-shadow:0 0 0 0 rgba(22,163,74,.4) } 60%{ box-shadow:0 0 0 10px rgba(22,163,74,0) } }
        @keyframes floatUp  { from{ opacity:0; transform:translateY(16px) } to{ opacity:1; transform:translateY(0) } }

        .fu  { animation: fadeUp .6s cubic-bezier(.22,.68,0,1.2) both; }
        .fi  { animation: fadeIn .55s ease both; }

        .nl  { color:rgba(255,255,255,.65); text-decoration:none; font-size:13px; font-weight:500; transition:color .15s; font-family:'Inter',sans-serif; }
        .nl:hover { color:#fff; }

        .btn { display:inline-flex; align-items:center; gap:8px; padding:12px 26px; border:none; border-radius:50px; font-size:14px; font-weight:700; cursor:pointer; transition:all .18s ease; text-decoration:none; font-family:'Inter',sans-serif; }
        .btn-fire  { background:${T.agniCore}; color:#fff; }
        .btn-fire:hover  { background:#C2410C; transform:translateY(-2px); box-shadow:0 8px 20px rgba(234,88,12,.42); }
        .btn-blue  { background:${T.blue}; color:#fff; }
        .btn-blue:hover  { background:#1D4ED8; transform:translateY(-2px); box-shadow:0 8px 20px rgba(37,99,235,.38); }
        .btn-gold  { background:${T.vidyaGold}; color:${T.nightInk}; }
        .btn-gold:hover  { background:#B5962E; transform:translateY(-2px); box-shadow:0 8px 20px rgba(200,168,75,.42); }
        .btn-green { background:${T.green}; color:#fff; }
        .btn-green:hover { background:#15803D; transform:translateY(-2px); box-shadow:0 8px 22px rgba(22,163,74,.45); }
        .btn-ghost { background:transparent; color:#fff; border:1.5px solid rgba(255,255,255,.28); }
        .btn-ghost:hover { background:rgba(255,255,255,.08); border-color:rgba(255,255,255,.65); }

        .fc { background:#fff; border-radius:16px; padding:22px 18px; border:1.5px solid #F0EDE8; transition:all .22s ease; position:relative; overflow:hidden; cursor:pointer; }
        .fc:hover { transform:translateY(-5px); box-shadow:0 18px 44px rgba(0,0,0,.09); border-color:transparent; }

        /* ── PATCH 2a: SRT card styles ── */
        .fc-srt { background:linear-gradient(135deg,#F0FDF4,#DCFCE7); border:2px solid ${T.green} !important; }
        .fc-srt:hover { transform:translateY(-6px) scale(1.015) !important; box-shadow:0 20px 52px rgba(22,163,74,.25) !important; }
        .srt-icon-ring { animation:srtPulse 2.4s ease-in-out infinite; }

        /* ── PATCH 4a: floating button ── */
        .srt-float { position:fixed; bottom:24px; right:24px; z-index:500; background:linear-gradient(135deg,${T.green},#15803D); color:#fff; border:none; border-radius:50px; padding:13px 20px; font-size:13px; font-weight:800; cursor:pointer; display:flex; align-items:center; gap:8px; box-shadow:0 6px 28px rgba(22,163,74,.5); transition:all .2s ease; font-family:'Inter',sans-serif; animation:floatUp .6s cubic-bezier(.22,.68,0,1.2) 1s both; white-space:nowrap; }
        .srt-float:hover { transform:translateY(-3px); box-shadow:0 10px 36px rgba(22,163,74,.6); }

        .wc { background:#fff; border-radius:18px; padding:28px 24px; border:1.5px solid #F0EDE8; transition:all .22s ease; }
        .wc:hover { transform:translateY(-4px); box-shadow:0 16px 40px rgba(0,0,0,.09); border-color:transparent; }

        .pt { overflow:hidden; border-radius:14px; position:relative; }
        .pt img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .5s ease; }
        .pt:hover img { transform:scale(1.06); }
        .pt-lbl { position:absolute; bottom:0; left:0; right:0; padding:18px 12px 10px; background:linear-gradient(to top,rgba(0,0,0,.58),transparent); color:#fff; font-size:11px; font-weight:600; letter-spacing:.4px; opacity:0; transition:opacity .25s; }
        .pt:hover .pt-lbl { opacity:1; }

        .sc { transition:transform .2s ease; text-align:center; padding:0 16px; position:relative; z-index:1; }
        .sc:hover { transform:translateY(-4px); }

        .ta { animation:popIn .42s cubic-bezier(.22,.68,0,1.2) both; }

        .chk { display:flex; gap:10px; align-items:flex-start; margin-bottom:10px; }
        .chk-dot { width:20px; height:20px; border-radius:50%; background:${T.green}; color:#fff; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; flex-shrink:0; margin-top:1px; }

        /* Partner logo card */
        .partner-card { background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1); border-radius:16px; padding:18px 24px; display:flex; align-items:center; gap:12px; transition:all .22s ease; }
        .partner-card:hover { background:rgba(255,255,255,.1); border-color:rgba(255,255,255,.2); transform:translateY(-2px); }

        /* Dark partner card */
        .partner-card-dark { background:#fff; border:1.5px solid #F0EDE8; border-radius:16px; padding:18px 24px; display:flex; align-items:center; gap:12px; transition:all .22s ease; box-shadow:0 2px 8px rgba(0,0,0,.05); }
        .partner-card-dark:hover { box-shadow:0 8px 28px rgba(0,0,0,.1); transform:translateY(-2px); border-color:transparent; }

        .divider { width:1px; height:36px; background:rgba(255,255,255,.12); }
        .divider-dark { width:1px; height:36px; background:#E5E7EB; }

        /* ── MOBILE RESPONSIVE — comprehensive ── */
        .nav-desktop { display:flex; }
        .nav-ham     { display:none; }

        /* ── 768px tablet/mobile breakpoint ── */
        @media (max-width: 768px) {
          /* Nav */
          .nav-desktop { display:none !important; }
          .nav-ham     { display:flex !important; }

          /* Hero */
          .hero-section {
            grid-template-columns:1fr !important;
            padding:86px 20px 56px !important;
            gap:28px !important;
            min-height:auto !important;
          }
          .hero-right   { display:none !important; }
          .hero-h1      { font-size:36px !important; }
          .hero-btns    { flex-direction:column !important; align-items:stretch !important; }
          .hero-btns .btn { justify-content:center !important; width:100% !important; }
          .srt-hero-card {
            flex-direction:column !important;
            text-align:center !important;
            gap:12px !important;
            align-items:center !important;
          }
          .srt-hero-card .btn { width:100% !important; justify-content:center !important; }

          /* Stats bar */
          .stats-grid   { grid-template-columns:1fr 1fr !important; }
          .stats-item:nth-child(odd)  { border-right:1px solid rgba(255,255,255,.07) !important; }
          .stats-item:nth-child(even) { border-right:none !important; }
          .stats-item   { border-bottom:1px solid rgba(255,255,255,.07) !important; padding:20px 12px !important; }

          /* Partner band */
          .partner-band-row { flex-direction:column !important; }
          .partner-band-row > div { min-width:unset !important; max-width:unset !important; width:100% !important; }

          /* Photo gallery */
          .photo-grid   { grid-template-columns:1fr 1fr !important; grid-template-rows:150px 150px 150px !important; }
          .photo-tall   { grid-row:auto !important; }

          /* Features */
          .feat-grid    { grid-template-columns:1fr 1fr !important; gap:10px !important; }

          /* Who it's for */
          .whofor-grid  { grid-template-columns:1fr !important; }

          /* How it works */
          .how-grid     { grid-template-columns:1fr 1fr !important; gap:32px 16px !important; }
          .how-line     { display:none !important; }

          /* Domains */
          .domains-grid { grid-template-columns:1fr !important; gap:36px !important; }

          /* About */
          .about-grid   { grid-template-columns:1fr !important; gap:28px !important; }
          .about-img    { height:240px !important; }

          /* Sections */
          .section-pad  {
            padding-left:20px !important;
            padding-right:20px !important;
            padding-top:56px !important;
            padding-bottom:56px !important;
          }

          /* Footer */
          .footer-top      { flex-direction:column !important; align-items:flex-start !important; gap:18px !important; }
          .footer-top > div:last-child { flex-direction:column !important; gap:10px !important; }
          .footer-partners { flex-direction:column !important; }
          .footer-partners .partner-card { min-width:unset !important; width:100% !important; }
          .footer-bottom   { flex-direction:column !important; align-items:flex-start !important; gap:6px !important; }

          /* CTA */
          .cta-btns { flex-direction:column !important; align-items:stretch !important; }
          .cta-btns .btn { justify-content:center !important; width:100% !important; }

          /* Floating SRT button */
          .srt-float span { display:none !important; }
          .srt-float { padding:14px !important; border-radius:50% !important; font-size:20px !important; bottom:16px !important; right:16px !important; width:52px; height:52px; justify-content:center; }

          /* Testimonial */
          .testimonial-card { padding:24px 18px !important; }

          /* Partner hero cards */
          .partner-card { padding:12px 14px !important; }
          .partner-card img { height:32px !important; }

          /* CTA section */
          .cta-section { padding:60px 20px !important; }

          /* Nav bar padding */
          nav { padding:0 20px !important; }
        }

        /* ── 480px small phone breakpoint ── */
        @media (max-width: 480px) {
          .feat-grid    { grid-template-columns:1fr !important; }
          .how-grid     { grid-template-columns:1fr !important; }
          .hero-h1      { font-size:28px !important; }
          .hero-section { padding:76px 16px 48px !important; }
          .stats-grid   { grid-template-columns:1fr 1fr !important; }
          .section-pad  { padding-left:16px !important; padding-right:16px !important; padding-top:48px !important; padding-bottom:48px !important; }
          .about-img    { height:200px !important; }

          /* Fix about floating badges on very small screens */
          .about-badge-left  { left:0 !important; }
          .about-badge-right { right:0 !important; bottom:-8px !important; }

          /* Testimonial smaller text */
          .testimonial-card p { font-size:14px !important; }

          /* Footer nav links wrap better */
          .footer-top > div:last-child { flex-direction:row !important; flex-wrap:wrap !important; gap:12px 20px !important; }

          /* Stats values smaller */
          .stats-val { font-size:36px !important; }

          /* Domains section — reduce font */
          .domains-grid h2 { font-size:28px !important; }

          nav { padding:0 16px !important; }
        }
      `}</style>

      {/* ═══════════════════════════════════════════
          NAV
      ═══════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300,
        background: scrollY > 60 ? 'rgba(10,22,40,.97)' : 'transparent',
        backdropFilter: scrollY > 60 ? 'blur(18px)' : 'none',
        borderBottom: scrollY > 60 ? '1px solid rgba(255,255,255,.06)' : 'none',
        transition: 'all .3s ease',
        padding: '0 56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 66, flexWrap: 'nowrap',
      }}>
        {/* Left: Vidyagni brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Mark size={36} />
          <div>
            <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 24, fontWeight: 900, color: '#fff', lineHeight: 1 }}>Vidyagni</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,.32)', letterSpacing: .8, marginTop: 2, fontFamily: "'Inter',sans-serif" }}>NISH · NCAHT · ICMR</div>
          </div>
          {/* Nav-bar partner mini-badges (visible when scrolled) */}
          {scrollY > 60 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 18, animation: 'slideIn .3s ease both' }}>
              <div className="divider" />
              {[
                { label: 'NISH', color: '#0891B2' },
                { label: 'NCAHT', color: '#2563EB' },
                { label: 'ICMR', color: '#FF9933' },
              ].map(b => (
                <span key={b.label} style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                  background: `${b.color}22`, color: b.color,
                  border: `1px solid ${b.color}44`, fontFamily: "'Inter',sans-serif",
                }}>{b.label}</span>
              ))}
            </div>
          )}
        </div>

        {/* Desktop nav */}
        <div className="nav-desktop" style={{ alignItems: 'center', gap: 30 }}>
          {[['#features','Features'],['#whofor',"Who it's for"],['#how','How it works'],['#domains','NCF Domains'],['#about','About']].map(([h,l]) => (
            <a key={h} href={h} className="nl">{l}</a>
          ))}
          {/* ── PATCH 3a: Free SRT button in nav ── */}
          <button className="btn btn-green" style={{ padding: '7px 16px', fontSize: 12 }} onClick={() => navigate('/quick-screener')}>🎮 Free SRT</button>
          <button className="btn btn-fire" style={{ padding: '8px 20px', fontSize: 13 }} onClick={() => navigate('/login')}>Sign In →</button>
        </div>

        {/* ── PATCH 3b: Mobile hamburger ── */}
        <button className="nav-ham" onClick={() => setMenuOpen(o => !o)}
          style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 8, padding: '7px 11px', cursor: 'pointer', color: 'white', fontSize: 17, lineHeight: 1 }}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </nav>

      {/* ── PATCH 3c: Mobile slide-down menu ── */}
      {menuOpen && (
        <div style={{ position: 'fixed', top: 66, left: 0, right: 0, zIndex: 299, background: 'rgba(10,22,40,.98)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,.08)', padding: '14px 24px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[['#features','Features'],['#whofor',"Who it's for"],['#how','How it works'],['#domains','NCF Domains'],['#about','About']].map(([h,l]) => (
            <a key={h} href={h} onClick={() => setMenuOpen(false)}
              style={{ color: 'rgba(255,255,255,.75)', textDecoration: 'none', fontSize: 15, fontWeight: 600, padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,.06)', fontFamily: "'Inter',sans-serif" }}>
              {l}
            </a>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14 }}>
            <button className="btn btn-green" style={{ justifyContent: 'center' }} onClick={() => { navigate('/quick-screener'); setMenuOpen(false) }}>🎮 Free School Readiness Test</button>
            <button className="btn btn-fire"  style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false) }}>Sign In to Vidyagni →</button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════ */}
      <section className="hero-section" style={{
        minHeight: '100vh',
        background: `linear-gradient(148deg, ${T.deepNavy} 0%, #0a1628 35%, #0d2145 65%, #091530 100%)`,
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        alignItems: 'center', padding: '96px 56px 72px', gap: 56,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* dot grid */}
        <div style={{ position:'absolute', inset:0, opacity:.032, backgroundImage:'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize:'28px 28px', pointerEvents:'none' }} />
        {/* glows */}
        <div style={{ position:'absolute', top:'18%', left:'26%', width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(234,88,12,.11) 0%,transparent 65%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'6%', right:'8%', width:320, height:320, borderRadius:'50%', background:'radial-gradient(circle,rgba(37,99,235,.11) 0%,transparent 65%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'55%', right:'42%', width:220, height:220, borderRadius:'50%', background:'radial-gradient(circle,rgba(200,168,75,.07) 0%,transparent 65%)', pointerEvents:'none' }} />

        {/* LEFT */}
        <div>
          {/* Institution badges */}
          <div className="fi" style={{ display:'flex', gap:6, marginBottom:28, flexWrap:'wrap', animationDelay:'.08s' }}>
            {['NISH Thiruvananthapuram','NCAHT','ICMR Funded'].map(b => (
              <span key={b} style={{ background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.11)', color:'rgba(255,255,255,.72)', fontSize:11, fontWeight:600, padding:'4px 11px', borderRadius:20, fontFamily:"'Inter',sans-serif" }}>{b}</span>
            ))}
          </div>

          {/* mark + wordmark */}
          <div className="fu" style={{ display:'flex', alignItems:'center', gap:14, marginBottom:18, animationDelay:'.12s' }}>
            <Mark size={58} />
            <div>
              <div className="hero-h1" style={{ fontFamily:"'Nunito',sans-serif", fontSize:60, fontWeight:900, color:'#fff', lineHeight:1, letterSpacing:-1 }}>
                <span style={{ background:`linear-gradient(90deg, ${T.agniGlow}, ${T.agniTip}, ${T.agniGlow})`, backgroundSize:'200% auto', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', animation:'shimmer 3.5s linear infinite' }}>Vidyagni</span>
              </div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,.38)', marginTop:5, fontFamily:"'Inter',sans-serif", fontWeight:500, letterSpacing:.5 }}>
                Foundational Stage Learning Platform
              </div>
            </div>
          </div>

          <p className="fu" style={{ fontSize:23, fontFamily:"'Nunito',sans-serif", fontWeight:900, color:'rgba(255,255,255,.88)', lineHeight:1.35, maxWidth:420, marginBottom:12, animationDelay:'.2s' }}>
            Igniting learning,<br />one child at a time.
          </p>

          <p className="fu" style={{ fontSize:15, color:'rgba(255,255,255,.48)', lineHeight:1.78, maxWidth:450, marginBottom:14, animationDelay:'.26s', fontFamily:"'Inter',sans-serif" }}>
            Assistive technology tools for special educators, clinicians, and parents — built for children aged 3–8 with special needs, fully aligned with <strong style={{ color:'rgba(255,255,255,.85)' }}>NCF-FS 2022</strong> and <strong style={{ color:'rgba(255,255,255,.85)' }}>NEP 2020</strong>.
          </p>

          <p className="fu" style={{ fontSize:10, color:'rgba(255,255,255,.25)', letterSpacing:2, marginBottom:24, animationDelay:'.28s', fontFamily:"'Inter',sans-serif" }}>
            MALAYALAM · HINDI · TAMIL · TELUGU · ENGLISH
          </p>

          {/* ── PATCH 2b: SRT hero card ── */}
          <div className="fu srt-hero-card" style={{ display:'flex', alignItems:'center', gap:14, background:'rgba(22,163,74,.14)', border:'1.5px solid rgba(22,163,74,.38)', borderRadius:16, padding:'16px 18px', marginBottom:24, animationDelay:'.32s' }}>
            <div style={{ width:48, height:48, borderRadius:12, background:'rgba(22,163,74,.22)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0, border:'2px solid rgba(22,163,74,.45)', animation:'srtPulse 2.4s ease-in-out infinite' }}>🎮</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:900, fontSize:14, color:'#fff', marginBottom:3, fontFamily:"'Nunito',sans-serif" }}>Free School Readiness Test for Parents</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.55)', lineHeight:1.5, fontFamily:"'Inter',sans-serif" }}>10-item Quick Screener assessment · No login needed · Print report instantly</div>
              
            </div>
            <button className="btn btn-green" style={{ flexShrink:0, fontSize:12, padding:'9px 16px', whiteSpace:'nowrap' }} onClick={() => navigate('/quick-screener')}>Start Free →</button>
          </div>

          {/* ── Partner logos lockup in hero ── */}
          <div className="fu" style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap', animationDelay:'.36s' }}>
            <div className="partner-card">
            <img src={NISHLogoWhite}  alt="NISH"  height={40} />
            </div>
            <div className="partner-card">
            <img src={NCAHTLogoWhite} alt="NCAHT" height={40} />
            </div>
            <div className="partner-card">
            <img src={ICMRLogoWhite}  alt="ICMR"  height={40} />
            </div>
          </div>

          <div className="fu hero-btns" style={{ display:'flex', gap:10, flexWrap:'wrap', animationDelay:'.4s' }}>
            <button className="btn btn-gold" style={{ fontSize:15, padding:'13px 30px' }} onClick={() => navigate('/login')}>Get Started →</button>
            <a href="#features" className="btn btn-ghost">Explore Platform</a>
          </div>

          {/* trust row */}
          <div className="fu" style={{ marginTop:28, display:'flex', alignItems:'center', gap:12, animationDelay:'.5s' }}>
            <div style={{ display:'flex' }}>
              {[T.agniCore, T.blue, T.violet, T.green].map((c, i) => (
                <div key={c} style={{ width:26, height:26, borderRadius:'50%', background:c, border:'2.5px solid rgba(8,15,30,.85)', marginLeft:i>0?-8:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff', fontWeight:800, fontFamily:"'Nunito',sans-serif" }}>
                  {['T','C','P','A'][i]}
                </div>
              ))}
            </div>
            <span style={{ fontSize:12, color:'rgba(255,255,255,.36)', fontFamily:"'Inter',sans-serif" }}>
              Used by <strong style={{ color:'rgba(255,255,255,.65)' }}>teachers, clinicians & parents</strong> at NISH
            </span>
          </div>
        </div>

        {/* RIGHT: photo collage — hidden on mobile via .hero-right */}
        <div className="hero-right fi" style={{ position:'relative', animationDelay:'.28s' }}>
          <div style={{ borderRadius:22, overflow:'hidden', height:430, boxShadow:'0 32px 80px rgba(0,0,0,.55)', border:'2px solid rgba(255,255,255,.05)' }}>
            <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&q=80" alt="Children learning" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.parentNode.style.background = 'rgba(37,99,235,.1)' }} />
          </div>
          <div style={{ position:'absolute', bottom:-18, left:-22, borderRadius:14, overflow:'hidden', width:150, height:114, boxShadow:'0 12px 32px rgba(0,0,0,.45)', border:'2px solid rgba(255,255,255,.07)' }}>
            <img src="https://images.unsplash.com/photo-1588072432836-e10032774350?w=400&q=80" alt="One on one" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { e.target.parentNode.style.background = 'rgba(234,88,12,.14)' }} />
          </div>
          <div style={{ position:'absolute', top:22, right:-18, background:T.agniCore, borderRadius:14, padding:'11px 17px', boxShadow:`0 8px 24px rgba(234,88,12,.46)` }}>
            <div style={{ fontSize:25, fontWeight:900, color:'#fff', fontFamily:"'Nunito',sans-serif", lineHeight:1 }}>8</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,.82)', fontWeight:600, marginTop:2, fontFamily:"'Inter',sans-serif" }}>Modules</div>
          </div>
          <div style={{ position:'absolute', top:92, right:-28, background:T.green, borderRadius:10, padding:'7px 13px', boxShadow:`0 5px 16px rgba(22,163,74,.42)` }}>
            <div style={{ fontSize:9, fontWeight:700, color:'rgba(255,255,255,.82)', fontFamily:"'Inter',sans-serif" }}>NCF-FS 2022</div>
            <div style={{ fontSize:11, fontWeight:700, color:'#fff', marginTop:1, fontFamily:"'Inter',sans-serif" }}>✓ Aligned</div>
          </div>
          <div style={{ position:'absolute', bottom:70, right:-22, background:T.nightInk, border:`1px solid ${T.vidyaGold}44`, borderRadius:10, padding:'7px 13px' }}>
            <div style={{ fontSize:9, fontWeight:700, color:T.vidyaGold, fontFamily:"'Inter',sans-serif", letterSpacing:.6 }}>विद्याग्नि</div>
            <div style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,.55)', marginTop:1, fontFamily:"'Inter',sans-serif" }}>NISH · Kerala</div>
          </div>
        </div>

        <div style={{ position:'absolute', bottom:28, left:'50%', color:'rgba(255,255,255,.18)', fontSize:18, animation:'drift 2.3s ease-in-out infinite' }}>↓</div>
      </section>

      {/* ═══════════════════════════════════════════
          STATS BAR
      ═══════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: T.nightInk, padding:'52px 56px' }}>
        <div className="stats-grid" style={{ maxWidth:980, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(4,1fr)' }}>
          {STATS.map((s, i) => (
            <div key={s.l} className="stats-item" style={{ textAlign:'center', padding:'0 20px', borderRight: i < 3 ? '1px solid rgba(255,255,255,.07)' : 'none' }}>
              <div style={{ fontSize:48, fontWeight:900, fontFamily:"'Nunito',sans-serif", color:T.agniGlow, lineHeight:1, marginBottom:8 }} className="stats-val">{s.v}</div>
              <div style={{ fontSize:13, fontWeight:700, color:'#fff', marginBottom:3, fontFamily:"'Inter',sans-serif" }}>{s.l}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.33)', fontFamily:"'Inter',sans-serif" }}>{s.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PARTNER INSTITUTIONS BAND
      ═══════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: T.vedaCream, padding:'52px 56px', borderTop:`1px solid #EDE8DF` }}>
        <div style={{ maxWidth:1060, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:32 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:2.5, fontFamily:"'Inter',sans-serif" }}>DEVELOPED &amp; FUNDED BY</div>
          </div>
          <div className="partner-band-row" style={{ display:'flex', justifyContent:'center', alignItems:'stretch', gap:16, flexWrap:'wrap' }}>
            <div className="partner-card-dark" style={{flex: '1', minWidth: 220, maxWidth: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={NISHLogo}  alt="NISH"  width={180} />
            </div>
            <div className="partner-card-dark" style={{flex: '1', minWidth: 220, maxWidth: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={NCAHTLogo} alt="NCAHT" width={180} />
            </div>
            <div className="partner-card-dark" style={{flex: '1', minWidth: 220, maxWidth: 320, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={ICMRLogo}  alt="ICMR"  width={180} />
            </div>
          </div>
          <p style={{ textAlign:'center', fontSize:12, color:T.muted, marginTop:20, fontFamily:"'Inter',sans-serif", lineHeight:1.6 }}>
            Vidyagni is a research initiative under the <strong style={{ color:T.slate }}>National Centre for Assistive Health Technologies (NCAHT)</strong> at NISH,<br />
            Thiruvananthapuram, supported by the <strong style={{ color:T.slate }}>Indian Council of Medical Research (ICMR)</strong>.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PHOTO GALLERY
      ═══════════════════════════════════════════ */}
      <section className="section-pad" style={{ padding:'80px 56px', background: '#F9F6F1' }}>
        <div style={{ maxWidth:1120, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:44 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.agniCore, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>IMPACT IN CLASSROOMS</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:38, fontWeight:900, color:T.slate, lineHeight:1.2 }}>Every child deserves to learn</h2>
            <p style={{ fontSize:15, color:T.muted, maxWidth:480, margin:'12px auto 0', lineHeight:1.75, fontFamily:"'Inter',sans-serif" }}>
              Supporting children with autism, hearing impairment, intellectual disability, and learning differences — in schools and at home across India.
            </p>
          </div>
          <div className="photo-grid" style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr 1fr', gridTemplateRows:'218px 218px', gap:10 }}>
            <div className="pt photo-tall" style={{ gridRow:'1/3' }}>
              <img src={PHOTOS[0].src} alt={PHOTOS[0].label} onError={e => { e.target.style.display='none'; e.target.parentNode.style.background='#E5E7EB' }} />
              <div className="pt-lbl">{PHOTOS[0].label}</div>
            </div>
            {PHOTOS.slice(1).map(p => (
              <div key={p.src} className="pt">
                <img src={p.src} alt={p.label} onError={e => e.target.style.display='none'} />
                <div className="pt-lbl">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FEATURES
      ═══════════════════════════════════════════ */}
      <section id="features" className="section-pad" style={{ padding:'92px 56px', background: T.ashWhite }}>
        <div style={{ maxWidth:1120, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:58 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.blue, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>PLATFORM MODULES</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:40, fontWeight:900, color:T.slate, lineHeight:1.2 }}>Everything a special educator needs</h2>
            <p style={{ fontSize:15, color:T.muted, maxWidth:480, margin:'12px auto 0', lineHeight:1.75, fontFamily:"'Inter',sans-serif" }}>
              Eight integrated modules, one platform. From first assessment to printed materials to parent communication — all in one place.
            </p>
          </div>
          {/* ── PATCH 2c: feature card grid ── */}
          <div className="feat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
            {FEATURES.map(f => (
              <div key={f.title}
                className={f.srt ? 'fc fc-srt' : 'fc'}
                onClick={f.srt ? () => navigate('/quick-screener') : () => navigate('/login')}
              >
                {/* Tag badge */}
                {f.tag && (
                  <div style={{ position:'absolute', top:11, right:11, fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:20, fontFamily:"'Inter',sans-serif",
                    background: f.srt ? T.green : f.tag==='New' ? T.green : f.tag==='Admin' ? T.blue : f.tag==='Most Used' ? '#FFF3ED' : '#EFF6FF',
                    color:      f.srt || f.tag==='New' || f.tag==='Admin' ? '#fff' : f.tag==='Most Used' ? T.agniCore : T.blue,
                  }}>{f.tag}</div>
                )}
                {/* Icon — pulsing ring on SRT card */}
                <div className={f.srt ? 'srt-icon-ring' : ''}
                  style={{ width:44, height:44, borderRadius:13, background: f.srt ? '#DCFCE7' : f.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:21, marginBottom:13, border: f.srt ? `2px solid ${T.green}` : 'none' }}>
                  {f.icon}
                </div>
                <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:15, fontWeight:800, color: f.srt ? '#15803D' : T.slate, marginBottom:7, lineHeight:1.3 }}>{f.title}</div>
                <div style={{ fontSize:13, color:T.muted, lineHeight:1.68, fontFamily:"'Inter',sans-serif" }}>{f.desc}</div>
                {/* CTA */}
                <div style={{ marginTop:14, fontSize:12, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
                  {f.srt
                    ? <span style={{ background:T.green, color:'#fff', padding:'4px 11px', borderRadius:20, fontSize:11 }}>🎮 Try Free Now →</span>
                    : <span style={{ color:f.color }}>Sign in to explore →</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          WHO IT'S FOR
      ═══════════════════════════════════════════ */}
      <section id="whofor" className="section-pad" style={{ padding:'92px 56px', background:'#F0F4FF' }}>
        <div style={{ maxWidth:1120, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:58 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.violet, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>BUILT FOR EVERYONE IN THE ROOM</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:40, fontWeight:900, color:T.slate, lineHeight:1.2 }}>Who is Vidyagni for?</h2>
            <p style={{ fontSize:15, color:T.muted, maxWidth:460, margin:'12px auto 0', lineHeight:1.75, fontFamily:"'Inter',sans-serif" }}>
              One platform, four audiences — each with a dedicated view and tailored set of tools.
            </p>
          </div>
          <div className="whofor-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
            {WHO_FOR.map(w => (
              <div key={w.role} className="wc">
                <div style={{ width:52, height:52, borderRadius:16, background:w.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, marginBottom:16 }}>{w.icon}</div>
                <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:18, fontWeight:900, color:w.color, marginBottom:14 }}>{w.role}</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {w.points.map(p => (
                    <div key={p} className="chk">
                      <div className="chk-dot" style={{ background:w.color }}>✓</div>
                      <div style={{ fontSize:13, color:T.muted, lineHeight:1.55, fontFamily:"'Inter',sans-serif" }}>{p}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════════════ */}
      <section id="how" className="section-pad" style={{ padding:'92px 56px', background: T.ashWhite }}>
        <div style={{ maxWidth:1060, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:60 }}>
            <div style={{ fontSize:10, fontWeight:700, color:T.agniCore, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>GET STARTED IN MINUTES</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:40, fontWeight:900, color:T.slate }}>How it works</h2>
            <p style={{ fontSize:15, color:T.muted, maxWidth:420, margin:'12px auto 0', lineHeight:1.75, fontFamily:"'Inter',sans-serif" }}>
              Four simple steps from registration to your first generated material.
            </p>
          </div>
          <div className="how-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', position:'relative' }}>
            <div className="how-line" style={{ position:'absolute', top:33, left:'12.5%', right:'12.5%', height:2, background:`linear-gradient(to right, ${T.blue}, ${T.violet}, ${T.agniCore}, ${T.green})`, borderRadius:2 }} />
            {HOW.map(s => (
              <div key={s.n} className="sc">
                <div style={{ width:66, height:66, borderRadius:'50%', background:s.c, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 22px', fontFamily:"'Nunito',sans-serif", fontSize:20, fontWeight:900, boxShadow:`0 8px 22px ${s.c}55`, border:`3px solid ${T.ashWhite}` }}>{s.n}</div>
                <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:16, fontWeight:800, color:T.slate, marginBottom:9 }}>{s.title}</div>
                <div style={{ fontSize:13, color:T.muted, lineHeight:1.68, fontFamily:"'Inter',sans-serif" }}>{s.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:'center', marginTop:52 }}>
            <button className="btn btn-fire" style={{ fontSize:15, padding:'13px 30px' }} onClick={() => navigate('/login')}>Start now →</button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          NCF DOMAINS
      ═══════════════════════════════════════════ */}
      <section id="domains" ref={domainsRef} className="section-pad" style={{ padding:'92px 56px', background: T.vedaCream }}>
        <div className="domains-grid" style={{ maxWidth:1000, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1.2fr', gap:72, alignItems:'center' }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:T.violet, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>NCF-FS 2022</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:36, fontWeight:900, color:T.slate, lineHeight:1.2, marginBottom:16 }}>Five developmental domains</h2>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.8, marginBottom:14, fontFamily:"'Inter',sans-serif" }}>
              Every module on the platform maps directly to India's National Curriculum Framework for the Foundational Stage — tracking each child's progress across all five key developmental areas.
            </p>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.8, marginBottom:14, fontFamily:"'Inter',sans-serif" }}>
              Domain data feeds into the Progress Tracker and parent-facing reports, giving a complete and evolving developmental picture for every child.
            </p>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.8, marginBottom:28, fontFamily:"'Inter',sans-serif" }}>
              Materials — flashcards, worksheets, work plans — are automatically tagged to the relevant domain so educators can see which areas are being addressed across sessions.
            </p>
            <button className="btn btn-fire" style={{ fontSize:13, padding:'10px 22px' }} onClick={() => navigate('/login')}>View Progress Tracker →</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            {DOMAINS.map((d, i) => (
              <div key={d.id}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:6 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:T.slate, fontFamily:"'Inter',sans-serif" }}>{d.id}</div>
                    <div style={{ fontSize:11, color:'#9CA3AF', fontFamily:"'Inter',sans-serif", marginTop:2 }}>{d.desc}</div>
                  </div>
                  <span style={{ fontSize:17, fontWeight:900, color:d.color, fontFamily:"'Nunito',sans-serif" }}>{d.pct}%</span>
                </div>
                <div style={{ height:10, background:'#E9E4DC', borderRadius:5, overflow:'hidden' }}>
                  <div style={{ width: domainsVis ? `${d.pct}%` : '0%', height:'100%', background:d.color, borderRadius:5, transition:`width 1.1s ease ${i*.16}s` }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop:8, padding:'14px 16px', background:'#fff', borderRadius:12, border:`1.5px solid ${T.vidyaGold}33`, display:'flex', gap:10, alignItems:'flex-start' }}>
              <div style={{ width:28, height:28, borderRadius:8, background:`${T.vidyaGold}22`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>📌</div>
              <div style={{ fontSize:12, color:T.muted, lineHeight:1.6, fontFamily:"'Inter',sans-serif" }}>
                <strong style={{ color:T.slate }}>Note:</strong> Domain percentages shown are illustrative engagement averages across NISH platform sessions. Each child's individual tracker is fully personalised.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════════════ */}
      <section className="section-pad" style={{ padding:'92px 56px', background: T.nightInk }}>
        <div style={{ maxWidth:760, margin:'0 auto', textAlign:'center' }}>
          <div style={{ fontSize:10, fontWeight:700, color:T.agniGlow, letterSpacing:2.5, marginBottom:10, fontFamily:"'Inter',sans-serif" }}>VOICES FROM THE FIELD</div>
          <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:36, fontWeight:900, color:'#fff', marginBottom:44 }}>Trusted by educators & clinicians</h2>
          <div key={tIdx} className="ta testimonial-card" style={{ background:'rgba(255,255,255,.05)', borderRadius:20, padding:'34px 42px', border:'1px solid rgba(255,255,255,.07)', marginBottom:26 }}>
            <div style={{ fontSize:42, color:T.agniGlow, lineHeight:1, marginBottom:12, fontFamily:"'Nunito',sans-serif" }}>"</div>
            <p style={{ fontSize:17, color:'rgba(255,255,255,.82)', lineHeight:1.78, marginBottom:26, fontStyle:'italic', fontFamily:"'Inter',sans-serif", fontWeight:400 }}>
              {TESTIMONIALS[tIdx].q}
            </p>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:TESTIMONIALS[tIdx].c, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'#fff', fontSize:12, fontFamily:"'Nunito',sans-serif" }}>
                {TESTIMONIALS[tIdx].av}
              </div>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#fff', fontFamily:"'Inter',sans-serif" }}>{TESTIMONIALS[tIdx].name}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', fontFamily:"'Inter',sans-serif" }}>{TESTIMONIALS[tIdx].role}</div>
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:7, justifyContent:'center' }}>
            {TESTIMONIALS.map((_, i) => (
              <button key={i} onClick={() => setTIdx(i)} style={{ width:i===tIdx?22:7, height:7, borderRadius:4, background:i===tIdx?T.agniGlow:'rgba(255,255,255,.16)', border:'none', cursor:'pointer', transition:'all .3s ease', padding:0 }} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          ABOUT
      ═══════════════════════════════════════════ */}
      <section id="about" className="section-pad" style={{ padding:'92px 56px', background: T.ashWhite }}>
        <div className="about-grid" style={{ maxWidth:1060, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:72, alignItems:'center' }}>
          <div style={{ position:'relative' }}>
            <div className="about-img" style={{ borderRadius:22, overflow:'hidden', height:420, boxShadow:'0 22px 56px rgba(0,0,0,.12)' }}>
              <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=700&q=80" alt="Children in class" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'} />
            </div>
            <div className="about-badge-right" style={{ position:'absolute', bottom:-16, right:-20, background:'#fff', borderRadius:14, padding:'14px 20px', boxShadow:'0 8px 32px rgba(0,0,0,.1)', border:'1.5px solid #F0EDE8' }}>
              <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:28, fontWeight:900, color:T.agniCore }}>3–8</div>
              <div style={{ fontSize:11, color:'#9CA3AF', fontFamily:"'Inter',sans-serif" }}>Years supported</div>
            </div>
            <div className="about-badge-left" style={{ position:'absolute', top:16, left:-14, background:T.green, color:'#fff', borderRadius:9, padding:'7px 12px', boxShadow:`0 5px 16px rgba(22,163,74,.38)` }}>
              <div style={{ fontSize:9, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>📍 Thiruvananthapuram</div>
              <div style={{ fontSize:10, fontWeight:600, marginTop:2, fontFamily:"'Inter',sans-serif" }}>Kerala, India</div>
            </div>
            <div style={{ position:'absolute', top:86, left:-14, background:T.nightInk, border:`1px solid ${T.vidyaGold}55`, borderRadius:9, padding:'7px 12px' }}>
              <div style={{ fontSize:9, fontWeight:700, color:T.vidyaGold, fontFamily:"'Inter',sans-serif", letterSpacing:.6 }}>विद्याग्नि</div>
              <div style={{ fontSize:9, color:'rgba(255,255,255,.45)', fontFamily:"'Inter',sans-serif", marginTop:1 }}>Flame of knowledge</div>
            </div>
          </div>

          <div>
            <div style={{ fontSize:10, fontWeight:700, color:T.blue, letterSpacing:2.5, marginBottom:12, fontFamily:"'Inter',sans-serif" }}>ABOUT VIDYAGNI</div>
            <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:36, fontWeight:900, color:T.slate, marginBottom:16, lineHeight:1.2 }}>Built at NISH for India's children with special needs</h2>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.85, marginBottom:14, fontFamily:"'Inter',sans-serif" }}>
              Developed by the <strong style={{ color:T.slate }}>National Centre for Assistive Health Technologies (NCAHT)</strong> at NISH, Thiruvananthapuram, in collaboration with ICMR.
            </p>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.85, marginBottom:24, fontFamily:"'Inter',sans-serif" }}>
              Vidyagni supports clinicians, special educators, and parents in creating personalised, evidence-based learning materials for children with autism, hearing impairment, intellectual disability, and learning differences.
            </p>
            {['Multi-language support across 5 Indian languages','Multi-tenant SaaS: schools, classrooms, subscription tiers','Role-based access: superadmin, teacher, clinician, parent','Print-ready A4 PDFs with NISH/NCAHT branding','Fully aligned to NCF-FS 2022 & NEP 2020 competencies'].map(item => (
              <div key={item} className="chk">
                <div className="chk-dot">✓</div>
                <div style={{ fontSize:14, color:'#4B5563', fontFamily:"'Inter',sans-serif", lineHeight:1.55 }}>{item}</div>
              </div>
            ))}

            {/* Partner logo row inside About */}
            <div style={{ display:'flex', gap:12, marginTop:28, flexWrap:'wrap', alignItems:'stretch' }}>
              <div className="partner-card-dark" style={{ flex:'1', minWidth:0 }}>
              <img src={NISHLogo}  alt="NISH"  width={100} />
              </div>
              <div className="partner-card-dark" style={{ flex:'1', minWidth:0 }}>
              <img src={NCAHTLogo} alt="NCAHT" width={100} />
              </div>
              <div className="partner-card-dark" style={{ flex:'1', minWidth:0 }}>
              <img src={ICMRLogo}  alt="ICMR"  width={100} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          CTA BANNER
      ═══════════════════════════════════════════ */}
      <section className="section-pad cta-section" style={{
        padding:'90px 56px',
        background:`linear-gradient(140deg, ${T.deepNavy} 0%, #0a1628 50%, #0d2145 100%)`,
        textAlign:'center', position:'relative', overflow:'hidden',
      }}>
        <div style={{ position:'absolute', inset:0, opacity:.03, backgroundImage:'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize:'26px 26px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'25%', left:'28%', width:420, height:420, borderRadius:'50%', background:`radial-gradient(circle, rgba(234,88,12,.1) 0%, transparent 65%)`, pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'40%', right:'22%', width:280, height:280, borderRadius:'50%', background:`radial-gradient(circle, rgba(200,168,75,.07) 0%, transparent 65%)`, pointerEvents:'none' }} />
        <div style={{ position:'relative' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:20 }}>
            <Mark size={54} />
          </div>
          <div style={{ fontSize:10, fontWeight:700, color:T.agniGlow, letterSpacing:2.5, marginBottom:14, fontFamily:"'Inter',sans-serif" }}>JOIN VIDYAGNI</div>
          <h2 style={{ fontFamily:"'Nunito',sans-serif", fontSize:44, fontWeight:900, color:'#fff', marginBottom:14, lineHeight:1.15 }}>
            Igniting learning,<br />one child at a time.
          </h2>
          <p style={{ fontSize:15, color:'rgba(255,255,255,.45)', maxWidth:400, margin:'0 auto 36px', lineHeight:1.75, fontFamily:"'Inter',sans-serif" }}>
            Sign in with your NISH credentials to access all 8 modules and start creating materials today.
          </p>
          <div className="cta-btns" style={{ display:'flex', gap:11, justifyContent:'center', flexWrap:'wrap' }}>
            <button className="btn btn-gold" style={{ fontSize:15, padding:'14px 34px' }} onClick={() => navigate('/login')}>Sign In to Vidyagni →</button>
            {/* ── PATCH 2d: CTA banner SRT button ── */}
            <button className="btn btn-green" style={{ fontSize:14, padding:'13px 26px' }} onClick={() => navigate('/quick-screener')}>🎮 Free Parent SRT</button>
            <a href="#features" className="btn btn-ghost" style={{ fontSize:14 }}>Explore Modules</a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════ */}
      <footer style={{ background:'#050C1A', padding:'44px 56px 32px' }} className="section-pad">
        <div style={{ maxWidth:1060, margin:'0 auto' }}>
          <div className="footer-top" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28, flexWrap:'wrap', gap:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Mark size={30} />
              <div>
                <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:15, fontWeight:900, color:'#fff', lineHeight:1 }}>Vidyagni</div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,.28)', marginTop:2, fontFamily:"'Inter',sans-serif" }}>Foundational Stage Learning Platform</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
              {[['#features','Features'],['#whofor',"Who it's for"],['#how','How it works'],['#domains','NCF Domains'],['#about','About']].map(([h,l]) => (
                <a key={h} href={h} style={{ color:'rgba(255,255,255,.25)', fontSize:12, textDecoration:'none', fontFamily:"'Inter',sans-serif", transition:'color .15s' }}
                  onMouseOver={e => e.target.style.color='rgba(255,255,255,.6)'}
                  onMouseOut={e => e.target.style.color='rgba(255,255,255,.25)'}>{l}</a>
              ))}
            </div>
          </div>

          <div className="footer-partners" style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
            <div className="partner-card" style={{ flex:'1', minWidth:200 }}>
            <img src={NISHLogoWhite}  alt="NISH"  width={140} />
            </div>
            <div className="partner-card" style={{ flex:'1', minWidth:200 }}>
            <img src={NCAHTLogoWhite} alt="NCAHT" width={140} />
            </div>
            <div className="partner-card" style={{ flex:'1', minWidth:200 }}>
            <img src={ICMRLogoWhite}  alt="ICMR"  width={140} />
            </div>
          </div>

          <div style={{ height:1, background:'rgba(255,255,255,.05)', marginBottom:18 }} />

          <div className="footer-bottom" style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8, alignItems:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.18)', fontFamily:"'Inter',sans-serif" }}>
              📍 Thiruvananthapuram, Kerala, India
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:10, color:T.vidyaGold, fontFamily:"'Inter',sans-serif", opacity:.6 }}>विद्याग्नि</span>
              <span style={{ fontSize:11, color:'rgba(255,255,255,.18)', fontFamily:"'Inter',sans-serif" }}>NCF-FS 2022 · NEP 2020 · v1.0 · © NISH NCAHT ICMR</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── PATCH 4b: floating SRT button ── */}
      <button className="srt-float" onClick={() => navigate('/quick-screener')}>
        🎮 <span>Free School Readiness Test</span>
      </button>

      {/* <PublicSRTModal open={srtOpen} onClose={() => setSrtOpen(false)} /> */}
    </div>
  )
}